
import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { useTheme } from "next-themes";
import { Sun, Moon, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";

export const AppearanceSection: React.FC = () => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Wait for component to mount to avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme);
    toast.success(`Mode ${newTheme === 'light' ? 'clair' : 'sombre'} activé`, {
      style: {
        backgroundColor: newTheme === 'dark' ? '#1e1e2d' : '#fff',
        color: newTheme === 'dark' ? '#fff' : '#1e1e2d',
        border: newTheme === 'dark' ? '1px solid #2d2d3d' : '1px solid #e2e8f0',
      },
    });
    
    // Force apply the theme class to document for immediate effect
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark')
      document.body.style.backgroundColor = "hsl(233, 42%, 11%)" // Darker version of #2D336B
    } else {
      document.documentElement.classList.remove('dark')
      document.body.style.backgroundColor = "#FAFBFD" // Set to our new pastel blue light
    }
    
    console.log("Theme changed to:", newTheme);
  };

  if (!mounted) {
    return null; // Avoid rendering until client-side
  }

  const currentTheme = theme || 'light';

  return (
    <div className="md:col-span-2">
      <Card className="p-6 shadow-card hover:shadow-card-elevated border border-gray-200 rounded-xl dark:bg-[rgba(30,32,47,0.6)] dark:border-[rgba(60,63,84,0.6)] dark:backdrop-blur-sm transition-all duration-300 settings-card">
        <h2 className="text-2xl font-bold mb-6 text-custom-dark dark:text-gray-100">Apparence</h2>

        {/* Theme Section */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-custom-primaryLight dark:bg-custom-primary/20">
              <LayoutDashboard className="h-5 w-5 text-custom-dark dark:text-custom-secondaryLight" />
            </div>
            <h3 className="font-medium text-custom-dark dark:text-gray-100">Thème</h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 ml-11">
            Sélectionnez le thème pour le tableau de bord.
          </p>

          <div className="grid grid-cols-2 gap-6 max-w-md ml-11">
            {/* Light Theme Option */}
            <div 
              className={`
                flex flex-col items-center border rounded-xl p-4 cursor-pointer transition-all 
                theme-option ${currentTheme === 'light' ? 'selected' : ''}
                ${currentTheme === 'light' 
                  ? 'border-custom-dark bg-pastel-blue-light shadow-md dark:bg-custom-primary/20 dark:border-custom-secondaryLight dark:shadow-none' 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm dark:border-gray-700 dark:hover:border-gray-600 dark:bg-[rgba(30,32,47,0.8)]'}
              `}
              onClick={() => handleThemeChange('light')}
            >
              <div className="p-4 bg-white border border-gray-200 rounded-lg w-full mb-3 shadow-sm dark:bg-gray-700 dark:border-gray-600">
                <div className="h-2 bg-gray-200 rounded mb-2 w-3/4 dark:bg-gray-600"></div>
                <div className="h-2 bg-gray-200 rounded mb-2 dark:bg-gray-600"></div>
                <div className="h-2 bg-gray-200 rounded w-1/2 dark:bg-gray-600"></div>
              </div>
              <div className="flex items-center gap-2">
                <Sun className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Clair</span>
              </div>
            </div>

            {/* Dark Theme Option */}
            <div 
              className={`
                flex flex-col items-center border rounded-xl p-4 cursor-pointer transition-all 
                theme-option ${currentTheme === 'dark' ? 'selected' : ''}
                ${currentTheme === 'dark' 
                  ? 'border-custom-primary bg-custom-primary/20 dark:border-custom-secondaryLight shadow-md' 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm dark:border-gray-700 dark:hover:border-gray-600 dark:bg-[rgba(30,32,47,0.8)]'}
              `}
              onClick={() => handleThemeChange('dark')}
            >
              <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg w-full mb-3 shadow-sm">
                <div className="h-2 bg-gray-600 rounded mb-2 w-3/4"></div>
                <div className="h-2 bg-gray-600 rounded mb-2"></div>
                <div className="h-2 bg-gray-600 rounded w-1/2"></div>
              </div>
              <div className="flex items-center gap-2">
                <Moon className="h-4 w-4 text-custom-secondaryLight" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sombre</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
